import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, CircularProgress } from "@mui/material";
import MainPage from "../MainPage";
import { cdFgbPathFor } from "../utils/cdVersion";

/**
 * SelfDrivingPage — /highlights/selfdriving
 *
 * A five-slide scroll story on comfort with self-driving vehicles, built on the
 * same architecture as the NJI reel (fixed map panel + snap column on desktop,
 * 30dvh map strip + free-scrolling cards on mobile) with Stack's teal styling.
 * Every headline number is computed from tracking wave 4 (July 2026):
 *   national comfort 36% vs 49% uncomfortable (net -13)
 *   only 35 of 436 districts net-positive, all dense urban
 *   age nets: 18-34 +32, 35-64 -6, 65+ -58 (a 90-point generation gap)
 *   education nets: degree +6, no degree -26 · gender nets: men -2, women -23
 *
 * The tracker's strongest single result is the wave-3 to wave-4 move, which the
 * COMFORT slide states as geography rather than as a net shift (see WaveMoveStat).
 * It belongs on that slide and not on the two-year-change slide, because it is a
 * fact about the comfort question:
 *   national net comfort -24.8 (April) → -12.7 (July), a +12.1 point move
 *   districts where comfort outweighs discomfort: 0 of 436 → 35 of 436
 *
 * The reel deliberately stays on wave 4 throughout. The wave-3-only questions
 * (what self-driving cars must demonstrate, and who has to be at the wheel) are
 * still in selfDriveAggregators.js and remain explorable in the app, but they
 * are kept out of the story so no slide mixes vintages. The summary's "Where is
 * the ceiling?" next-step is the hook for re-fielding them.
 *
 * ── FINDINGS COMPUTED BUT DELIBERATELY NOT IN THE REEL ───────────────────────
 * Kept here because they are verified and will come up in conversation, not
 * because anything renders them. All wave 4 unless stated.
 *
 * 2024 vote is a NON-divide, and a striking one: Trump -13.10 vs Harris -13.68 on
 * comfort today (0.58 points apart), and +4.26 vs +4.24 on the two-year question
 * (0.02 apart). Non-voters -10.94. Use vote24, NOT `party`: party is coded
 * four-way in wave 3 (~91M unaffiliated) and three-way leaned in wave 4
 * (~70/66/70M) so it cannot be tracked across waves, whereas vote24 is identical
 * in both. vote24 coding, established by cross-tabbing against party and checking
 * against the real 2024 result: 1=Trump (77.1M), 2=Harris (74.8M), 3=other
 * (3.1M), 4=did not vote (50.7M) — the first two land within ~0.2M of the actual
 * national counts.
 *
 * The national near-tie is two opposing gradients cancelling. Among under-35s
 * Trump voters are 8.0 points MORE comfortable than Harris voters; among over-65s
 * 4.1 points less. It survives controls (+12.1 within graduates, +12.3 within
 * non-graduates) so it is not composition, but it is a two-way cross of MODELLED
 * estimates and must not be presented as an observed interaction in raw responses.
 *
 * Subgroups compound. age × gender × education gives twelve blocs spanning 125
 * points, +57.9 (18-34 men, degree, 8.3M) to -67.2 (65+ women, no degree, 21.6M).
 * Push to a four-way with vote and it stretches to +65.6 / -71.3, but those cells
 * fall to ~3M. Note that Trump voters occupy BOTH ends.
 *
 * The country warmed UNIFORMLY, which matters because the retrospective question
 * implies otherwise. Measured wave-3 → wave-4 gain in district net comfort:
 * +10.6 for the leading tier, +11.9 mid, +11.8 laggards, r = -0.13 against
 * starting level. Every tier gained about the same, so the distribution shifted
 * right in parallel and the map is NOT diverging.
 *
 * The two-year-change question disagrees, and it is the question that is wrong for
 * this purpose: on it the 35 leaders score +19.5 against +6.4 for the 267
 * districts that are gaining but not yet net-comfortable. That 3x gradient is a
 * self-report effect (people comfortable now are likelier to SAY they became more
 * comfortable), not measured movement. Practical consequence: the momentum slide's
 * map is closer to a second comfort map than to a change map, which is why the two
 * correlate at r = 0.949. Be ready for that question rather than surprised by it.
 *
 * The 2x2 of already-comfortable × becoming-more-comfortable has an empty cell:
 * 35 are both, 267 are gaining but not yet over, 134 are neither, and ZERO are
 * net-comfortable without still gaining. Nothing has plateaued anywhere.
 *
 * NOT worth using: districts where momentum runs ahead of level. Residual sd is
 * only 2.4 points and the top of that list is Florida retirement seats where both
 * numbers are poor and the change is merely less poor. Ranking artefact.
 *
 * Geography is demographics. The 35 net-comfortable districts average 19% under-35
 * graduates against 8% across the other 401. Under-35-graduate share runs 3% to
 * 29% by district, over-65-non-graduate share 7% to 30%. A composition index of
 * those two tracks district net comfort at r = 0.95 and pins it within ~3 points.
 * Caveat: these are MRP estimates built partly from demographic composition, so
 * that correlation is substantially mechanical, not an empirical discovery.
 *
 * Data note: wave 4 carries an empty placeholder row for DC (dist_fips 1100, all
 * nulls) alongside the real 1198. It dilutes national estimates by ~0.03pp and
 * inflates a naive district count to 437. Filter it out of any new district work.
 */

// ─── Step definitions ─────────────────────────────────────────────────────────
const STEPS = [
  {
    name: "intro",
    // The intro leads on the finding rather than on a title. Kept here rather
    // than hardcoded in IntroCard so the dot-nav tooltip and the card read from
    // one source, as every other slide does.
    headline: "America says it has grown more comfortable with self-driving cars, and the young are fueling this growth.",
    copy: "35% report growing more comfortable over the last two years against 31% less, led by a net +43 among under-35s, with most districts leaning toward more.",
    mapLabel: "Net change in comfort vs two years ago by congressional district",
    // Identical to the momentum slide's mapView so revealing the first finding
    // changes nothing on the map: it loads once behind the blurred hero.
    mapView: { variable: "self_drive_change", geoLevel: "cd", preferenceFilter: "net", viewMode: "heat" },
    flyTo: { type: "fitBounds", bounds: [[-125, 24], [-66, 49]], duration: 0 },
  },
  {
    name: "momentum",
    headline: "America says it's warming to self-driving cars.",
    copy: "Asked directly about the last two years, more Americans say they have grown more comfortable than less, and most of the country's districts lean toward more.",
    mapLabel: "Net change in comfort vs two years ago by congressional district",
    mapView: { variable: "self_drive_change", geoLevel: "cd", preferenceFilter: "net", viewMode: "heat" },
    flyTo: { type: "fitBounds", bounds: [[-125, 24], [-66, 49]], duration: 1200 },
    mapHoverHint: true,
  },
  {
    name: "comfort-map",
    headline: "Despite this, today most Americans aren't yet comfortable.",
    copy: "The trend is up, but the level still favors discomfort, and the exceptions are the districts with the youngest, most educated populations.",
    mapLabel: "Net comfort with self-driving vehicles by congressional district",
    mapView: { variable: "self_drive_comfort", geoLevel: "cd", preferenceFilter: "net", viewMode: "heat" },
    flyTo: { type: "fitBounds", bounds: [[-125, 24], [-66, 49]], duration: 1200 },
    mapHoverHint: true,
  },
  {
    name: "demographics",
    headline: "The warming is generational.",
    copy: "Under-35s say they have grown more comfortable by a net +43. Over-65s are still moving away. Every other divide is smaller than this one.",
    mapLabel: "Net two-year change by congressional district, filterable by age",
    mapView: { variable: "self_drive_change", geoLevel: "cd", preferenceFilter: "net", viewMode: "heat" },
    flyTo: { type: "fitBounds", bounds: [[-125, 24], [-66, 49]], duration: 1200 },
    mapHoverHint: true,
  },
  {
    name: "summary",
    headline: "The road ahead.",
    copy: "",
    mapLabel: "Net comfort with self-driving vehicles by congressional district",
    mapView: { variable: "self_drive_comfort", geoLevel: "cd", preferenceFilter: "net", viewMode: "heat" },
    flyTo: { type: "fitBounds", bounds: [[-125, 24], [-66, 49]], duration: 1400 },
  },
];

const QUESTION_TEXT = {
  comfort: "How comfortable or uncomfortable would you feel about personally using a self-driving vehicle for day-to-day travel?",
  change: "Compared to two years ago, have you become more comfortable or less comfortable with the idea of riding in a self-driving vehicle?",
};

// National toplines per wave (weighted, computed from the tracking CD table),
// so the card's bars move with the map when the wave toggle flips.
const COMFORT_ROWS_BY_WAVE = {
  3: [
    { key: "model_self_driving_very_comf_pred_n", label: "Very comfortable", pct: 11, color: "#0d9488" },
    { key: "model_self_driving_somewhat_comf_pred_n", label: "Somewhat comfortable", pct: 18, color: "#5eead4" },
    { key: "model_self_driving_neither_pred_n", label: "Neither", pct: 16, color: "#94a3b8" },
    { key: "model_self_driving_somewhat_uncomf_pred_n", label: "Somewhat uncomfortable", pct: 19, color: "#fda4af" },
    { key: "model_self_driving_very_uncomf_pred_n", label: "Very uncomfortable", pct: 35, color: "#e11d48" },
  ],
  4: [
    { key: "model_self_driving_very_comf_pred_n", label: "Very comfortable", pct: 15, color: "#0d9488" },
    { key: "model_self_driving_somewhat_comf_pred_n", label: "Somewhat comfortable", pct: 21, color: "#5eead4" },
    { key: "model_self_driving_neither_pred_n", label: "Neither", pct: 16, color: "#94a3b8" },
    { key: "model_self_driving_somewhat_uncomf_pred_n", label: "Somewhat uncomfortable", pct: 18, color: "#fda4af" },
    { key: "model_self_driving_very_uncomf_pred_n", label: "Very uncomfortable", pct: 31, color: "#e11d48" },
  ],
};

// "Compared to two years ago" (wave 4 only) — the client's question.
const CHANGE_ROWS = [
  { key: "model_self_driving_change_MM_comf_pred_n", label: "Much more comfortable", pct: 14, color: "#0d9488" },
  { key: "model_self_driving_change_SM_comf_pred_n", label: "Somewhat more comfortable", pct: 21, color: "#5eead4" },
  { key: "model_self_driving_change_same_pred_n", label: "About the same", pct: 34, color: "#94a3b8" },
  { key: "model_self_driving_change_SL_comf_pred_n", label: "Somewhat less comfortable", pct: 10, color: "#fda4af" },
  { key: "model_self_driving_change_ML_comf_pred_n", label: "Much less comfortable", pct: 21, color: "#e11d48" },
];

const MOMENTUM_CDS = {
  movers:  ["3612", "0611", "3610", "4837", "0801", "5307"], // NY-12, CA-11, NY-10, TX-37, CO-01, WA-07
  coolers: ["3701", "4002", "2802"],                         // NC-01, OK-02, MS-02
};

// The subgroup story as a single ranked ladder rather than one chart per
// dimension. Age, education and gender compound: the twelve cells of
// age × gender × education span 125 points of net comfort, from +57.9 to -67.2,
// and every one of them is a real bloc (8M to 31M adults). Wave 4, weighted.
//
// Two things this makes visible that a per-dimension breakdown hides. First, the
// gradient is not symmetric: the most opposed bloc (65+ women without a degree,
// 21.6M) is bigger than the most supportive (under-35 men with a degree, 8.3M),
// and the four most-opposed blocs together outweigh the four most supportive.
// Second, the populous middle sits near zero, which is where the movement is.
// Age × education, six blocs, wave 4 weighted. Deliberately NOT crossed with
// gender as well: that version runs to twelve rows spanning 125 points, which is
// a more complete picture and a worse slide. Six rungs stay scannable, the ladder
// is monotonic, and every bloc is 17M adults or more.
//
// `age` and `education` are the codes the map filter takes, so selecting a rung
// re-maps the country for that bloc. `areas` are the three districts where the
// bloc is densest, outlined on selection.
// Net "gotten more comfortable" by group (more − less, wave 4).
const CHANGE_DEMO_ROWS = {
  age: [
    { label: "18–34", comf: 58, unc: 15, net: 43 },
    { label: "35–64", comf: 37, unc: 29, net: 9 },
    { label: "65+", comf: 14, unc: 46, net: -33 },
  ],
  education: [
    { label: "Degree", comf: 45, unc: 22, net: 23 },
    { label: "No degree", comf: 28, unc: 37, net: -9 },
  ],
  gender: [
    { label: "Men", comf: 40, unc: 27, net: 13 },
    { label: "Women", comf: 30, unc: 35, net: -4 },
  ],
  // By 2024 presidential vote, and the point is that there is no gap: +4.26 vs
  // +4.24 unrounded, which is why both rows read +4. Shown alongside the real
  // divides deliberately, so the flat pair reads as a finding rather than an
  // omission. Uses vote24, NOT `party` (see the file header for why).
  vote: [
    { label: "Trump", comf: 36, unc: 31, net: 4 },
    { label: "Harris", comf: 36, unc: 31, net: 4 },
  ],
};

const DEMO_NET_ROWS = {
  age: [
    { label: "18–34", comf: 57, unc: 25, net: 32 },
    { label: "35–64", comf: 39, unc: 44, net: -6 },
    { label: "65+", comf: 15, unc: 73, net: -58 },
  ],
  education: [
    { label: "Degree", comf: 47, unc: 41, net: 6 },
    { label: "No degree", comf: 28, unc: 54, net: -26 },
  ],
  gender: [
    { label: "Men", comf: 41, unc: 43, net: -2 },
    { label: "Women", comf: 31, unc: 54, net: -23 },
  ],
};

// ─── AppPanelWrapper ──────────────────────────────────────────────────────────
const AppPanelWrapper = React.forwardRef(function AppPanelWrapper({ interactive = false }, ref) {
  const noopNavigate = useCallback(() => {}, []);
  return (
    <MainPage
      ref={ref}
      slug="selfdrive"
      navigate={noopNavigate}
      hideChrome
      skipAuthRedirect
      disableMapInteraction={!interactive}
      geoPreloadOrder={["cd"]}
    />
  );
});

// ─── Navigation dispatcher ────────────────────────────────────────────────────
let _cameraTimer = null;
let _lastFlyKey = "";
const RESET_HIGHLIGHTS_STATE = {
  gender: null,
  age: null,
  ethnicity: null,
  party: null,
  education: null,
  vote24: null,
  heatDomain: null,
  heatColor: null,
  topStrengthShading: false,
};

function getGlMap(mainInst) {
  return mainInst?._mapPageInstance?.mapRef?.current?.getMap?.() || null;
}

function exitHighlights(navigate) {
  if (window.innerWidth < 600) {
    try { sessionStorage.setItem("m.activeScreen", "map"); } catch (e) { /* ignore */ }
    navigate("/m");
  } else {
    navigate("/");
  }
}

// MOBILE ONLY: fit camera moves with zero padding so the US fills the strip.
function flyForViewport(fly) {
  return window.innerWidth < 600 ? { ...fly, padding: 0 } : fly;
}

function armCrossfade(glMap, duration = 550) {
  if (!glMap) return;
  let style;
  try { style = glMap.getStyle(); } catch { return; }
  if (!style?.layers) return;
  const t = { duration, delay: 0 };
  style.layers.forEach((layer) => {
    try {
      if (layer.type === "fill") {
        glMap.setPaintProperty(layer.id, "fill-color-transition", t);
        glMap.setPaintProperty(layer.id, "fill-opacity-transition", t);
        glMap.setPaintProperty(layer.id, "fill-outline-color-transition", t);
      } else if (layer.type === "line") {
        glMap.setPaintProperty(layer.id, "line-color-transition", t);
        glMap.setPaintProperty(layer.id, "line-opacity-transition", t);
      }
    } catch { /* safe */ }
  });
}

function navigateToStep(mainInst, stepName, extraMapState = {}) {
  if (!mainInst) return;
  const step = STEPS.find((s) => s.name === stepName);
  if (!step) return;

  if (_cameraTimer) { clearTimeout(_cameraTimer); _cameraTimer = null; }

  mainInst.setState({ chatHighlightAreas: [] });
  armCrossfade(getGlMap(mainInst));

  if (step.mapView) {
    mainInst.setHighlightsState({ ...RESET_HIGHLIGHTS_STATE, ...step.mapView, ...extraMapState });
  }

  if (step.flyTo) {
    const key = JSON.stringify(step.flyTo);
    if (key !== _lastFlyKey) {
      _lastFlyKey = key;
      _cameraTimer = setTimeout(() => {
        _cameraTimer = null;
        mainInst.flyTo(flyForViewport(step.flyTo));
      }, 80);
    }
  }
}

// ─── Style tokens (Stack teal, matching PredictionMarketsPage) ────────────────
const MOBILE_STRIP_H = "30dvh";
const F = "'Inter', 'DM Sans', sans-serif";;
const TEAL = "#0d9488";
const TEAL_DARK = "#0f766e";
const INK = "#0f172a";
const MUTED = "#475569";
const MUTED_LIGHT = "#94a3b8";
const PAPER = "#ffffff";
const PAPER_SOFT = "linear-gradient(160deg, rgba(255,255,255,1) 0%, rgba(248,250,252,0.98) 100%)";
const BORDER = "rgba(13,148,136,0.1)";
const BG = "#ffffff";
const COMF = "#0d9488";
const UNCOMF = "#e11d48";

// ─── Animation helpers ────────────────────────────────────────────────────────
const FADE_UP_KEYFRAMES = {
  "@keyframes fadeUpIn": {
    from: { opacity: 0, transform: "translateY(14px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
};
const fadeUp = (delay) => ({
  animation: `fadeUpIn 0.52s cubic-bezier(0.22,1,0.36,1) ${delay}s both`,
  ...FADE_UP_KEYFRAMES,
});

// ─── Shared sub-components ────────────────────────────────────────────────────
function StackLogo() {
  return (
    <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
      <img src="/static/images/logo.png" alt="District Profiles" style={{ height: 22, width: "auto", display: "block" }} />
    </Box>
  );
}

function Eyebrow({ children }) {
  return (
    <Typography sx={{ fontSize: { xs: "11.5px", sm: "10px" }, fontWeight: 800, letterSpacing: "0.16em", textTransform: "uppercase", color: TEAL, fontFamily: F, mb: 1.4 }}>
      {children}
    </Typography>
  );
}

function Headline({ children }) {
  return (
    <Typography sx={{ fontSize: { xs: "21px", sm: "26px", md: "30px" }, fontWeight: 600, lineHeight: { xs: 1.16, md: 1.1 }, letterSpacing: "-0.02em", color: INK, fontFamily: F, mb: 1.3 }}>
      {children}
    </Typography>
  );
}

function BodyCopy({ children }) {
  return (
    <Typography sx={{ fontSize: { xs: "14.5px", sm: "13.5px", md: "14px" }, color: MUTED, lineHeight: 1.5, fontFamily: F, whiteSpace: "pre-line", letterSpacing: "-0.01em" }}>
      {children}
    </Typography>
  );
}

function Em({ children }) {
  return <Box component="strong" sx={{ fontWeight: 700, color: INK }}>{children}</Box>;
}

function ClickHint({ children, mobileChildren }) {
  return (
    <Box sx={{ mt: 1.6, px: 1.5, py: 1.1, borderRadius: "10px", bgcolor: `${TEAL}08`, border: `1px solid ${TEAL}20`, display: "flex", alignItems: "flex-start", gap: 1.1 }}>
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
        <path d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5" />
      </svg>
      <Typography sx={{ fontSize: { xs: "13.5px", sm: "12px" }, color: INK, lineHeight: 1.4, fontFamily: F, display: mobileChildren ? { xs: "none", sm: "block" } : "block" }}>
        {children}
      </Typography>
      {mobileChildren && (
        <Typography sx={{ fontSize: { xs: "13.5px", sm: "12px" }, color: INK, lineHeight: 1.4, fontFamily: F, display: { xs: "block", sm: "none" } }}>
          {mobileChildren}
        </Typography>
      )}
    </Box>
  );
}

const HintStrong = ({ children }) => <strong style={{ color: TEAL }}>{children}</strong>;

function Panel({ children, sx = {}, ...props }) {
  return (
    <Box {...props} sx={{ width: "100%", maxWidth: 590, p: { xs: 2, md: 2.5 }, borderRadius: "22px", bgcolor: PAPER, backgroundImage: PAPER_SOFT, border: `1px solid ${BORDER}`, boxShadow: "0 8px 28px rgba(13,148,136,0.06)", ...sx }}>
      {children}
    </Box>
  );
}

// `onClick` turns the box into a map control: it grows a pill at the bottom that
// labels what the click does, and lifts its border while the highlight is live.
function InsightBox({ color, title, stat, desc, onClick, active = false, actionLabel, activeLabel }) {
  const clickable = typeof onClick === "function";
  return (
    <Box
      onClick={onClick}
      sx={{
        height: "100%", px: 1.5, py: 1.25, borderRadius: "12px",
        border: `1px solid ${active ? color : `${color}25`}`,
        bgcolor: active ? `${color}0a` : PAPER,
        boxShadow: active ? `0 8px 28px ${color}2e` : "0 8px 28px rgba(13,148,136,0.06)",
        transition: "border-color 0.18s ease, background-color 0.18s ease, box-shadow 0.18s ease",
        ...(clickable && {
          cursor: "pointer", userSelect: "none", WebkitTapHighlightColor: "transparent",
          "&:hover": { borderColor: color, boxShadow: `0 10px 30px ${color}2e` },
        }),
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mb: 0.5 }}>
        <Box sx={{ width: 6, height: 6, borderRadius: "2px", bgcolor: color, flexShrink: 0 }} />
        <Typography sx={{ fontSize: { xs: "13px", sm: "11px" }, fontWeight: 700, color, fontFamily: F, letterSpacing: "0.04em", textTransform: "uppercase" }}>{title}</Typography>
      </Box>
      {stat && <Typography sx={{ fontSize: "22px", fontWeight: 800, color: INK, fontFamily: F, lineHeight: 1, letterSpacing: "-0.03em", mb: 0.5 }}>{stat}</Typography>}
      <Typography sx={{ fontSize: { xs: "13.5px", sm: "11.5px" }, color: MUTED, fontFamily: F, lineHeight: 1.5 }}>{desc}</Typography>
      {clickable && (
        <Box sx={{ mt: 0.9, display: "inline-flex", alignItems: "center", gap: 0.6, px: 1, py: 0.55, borderRadius: "999px", bgcolor: active ? color : `${color}12`, border: `1px solid ${active ? color : `${color}30`}`, transition: "all 0.18s ease" }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={active ? "#fff" : color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <path d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5" />
          </svg>
          <Typography sx={{ fontSize: { xs: "12px", sm: "10px" }, fontWeight: 700, fontFamily: F, color: active ? "#fff" : color, lineHeight: 1, letterSpacing: "0.01em" }}>
            {active ? (activeLabel || "Clear the map") : (actionLabel || "Show on the map")}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function ResponseBarRow({ label, value, color }) {
  return (
    <Box sx={{ display: "grid", gap: 0.45 }}>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.8 }}>
        <Box sx={{ width: 7, height: 7, borderRadius: "2px", bgcolor: color, flexShrink: 0 }} />
        <Typography sx={{ flex: 1, fontSize: { xs: "13.5px", sm: "11.5px" }, fontWeight: 700, color: INK, fontFamily: F, lineHeight: 1.35 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: "15px", fontWeight: 800, color, fontFamily: F, lineHeight: 1 }}>
          {value}%
        </Typography>
      </Box>
      <Box sx={{ height: 6, borderRadius: "999px", bgcolor: `${color}16`, overflow: "hidden" }}>
        <Box sx={{ width: `${Math.max(0, Math.min(100, value))}%`, height: "100%", bgcolor: color, borderRadius: "999px" }} />
      </Box>
    </Box>
  );
}

// One rung of the persona ladder: a bar diverging from a centre line, so the
// sign flip partway down the list is visible as a shape rather than read off the
// numbers. Population sits on the right because bloc size is half the story.
// Comfortable-vs-uncomfortable split bar with a net readout.
function NetRow({ label, comf, unc, net }) {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "76px 1fr 56px", gap: 1, alignItems: "center" }}>
      <Typography sx={{ fontSize: { xs: "13px", sm: "11px" }, fontWeight: 700, color: INK, fontFamily: F }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", height: 8, borderRadius: "999px", overflow: "hidden", bgcolor: "rgba(15,23,42,0.06)" }}>
        <Box sx={{ width: `${comf}%`, bgcolor: COMF }} />
        <Box sx={{ width: `${100 - comf - unc}%`, bgcolor: "transparent" }} />
        <Box sx={{ width: `${unc}%`, bgcolor: UNCOMF, opacity: 0.85 }} />
      </Box>
      <Typography sx={{ fontSize: { xs: "13px", sm: "11px" }, fontWeight: 800, color: net >= 0 ? TEAL_DARK : UNCOMF, fontFamily: F, textAlign: "right" }}>
        {net > 0 ? `+${net}` : net}
      </Typography>
    </Box>
  );
}

// The wave-3 to wave-4 move, stated as geography rather than as a net shift.
// "0 of 436 districts to 35 of 436 in one quarter" is the same fact as "+12 net"
// but it is the version that means something to anyone who works district by
// district, and it is the strongest single number the tracker has produced.
//
// Rendered as a MAP overlay rather than a card block: it is a statement about the
// map, so it belongs on top of it. Top-centre, because MapQuestionLegend owns
// bottom-centre. Hidden on xs, where the map is a 30dvh strip with no room.
function WaveMoveStat() {
  const cell = (month, value, color) => (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 34 }}>
      <Typography sx={{ fontSize: "22px", fontWeight: 600, color, fontFamily: F, lineHeight: 1, letterSpacing: "-0.02em" }}>
        {value}
      </Typography>
      <Typography sx={{ mt: 0.3, fontSize: "8.5px", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: MUTED_LIGHT, fontFamily: F }}>
        {month}
      </Typography>
    </Box>
  );
  return (
    <Box sx={{
      position: "absolute", top: 14, left: "50%", transform: "translateX(-50%)", zIndex: 11,
      display: { xs: "none", sm: "flex" }, flexDirection: "column", alignItems: "center", gap: 0.55,
      maxWidth: "calc(100% - 28px)", px: 1.6, py: 1, borderRadius: "12px",
      bgcolor: "rgba(255,255,255,0.9)", backdropFilter: "blur(12px)",
      border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 8px 26px rgba(15,23,42,0.10)",
      textAlign: "center", animation: "fadeUpIn 0.4s ease both", ...FADE_UP_KEYFRAMES,
    }}>
      <Typography sx={{ fontSize: "8.5px", fontWeight: 800, letterSpacing: "0.09em", textTransform: "uppercase", color: TEAL, fontFamily: F, lineHeight: 1.3 }}>
        Net-comfortable districts
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
        {cell("Apr", "0", UNCOMF)}
        <Box sx={{ color: MUTED_LIGHT, display: "flex", alignItems: "center", pb: 1.2 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 6 15 12 9 18" /></svg>
        </Box>
        {cell("Jul", "35", TEAL_DARK)}
      </Box>
      <Typography sx={{ fontSize: "9px", color: MUTED, fontFamily: F, lineHeight: 1.35 }}>
        of 436 · net −25 → −13
      </Typography>
    </Box>
  );
}



function MethodNote() {
  return (
    <Typography sx={{ mt: 1.25, fontSize: { xs: "11.5px", sm: "9.5px" }, color: MUTED_LIGHT, fontFamily: F, lineHeight: 1.5 }}>
      MRP estimates, n=8,000, July 2026.
    </Typography>
  );
}

function StepHeader({ index, total, chapter, isActive, onScrollNext, hasNext, onScrollPrev }) {
  const hasPrev = index > 0 && typeof onScrollPrev === "function";
  return (
    <Box sx={{ display: { xs: "none", sm: "flex" }, alignItems: "center", gap: 1, mt: "auto", pt: 1.5, opacity: isActive ? 1 : 0, transition: "opacity 0.35s ease", minHeight: 28 }}>
      <Typography sx={{ fontSize: "10px", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: TEAL, fontFamily: F }}>
        {index + 1} of {total}
      </Typography>
      {chapter && (
        <Typography sx={{ fontSize: "10px", color: MUTED_LIGHT, fontFamily: F, letterSpacing: "0.02em" }}>
          · {chapter}
        </Typography>
      )}
      {hasPrev && (
        <Box
          onClick={onScrollPrev}
          sx={{ ml: "auto", display: "inline-flex", alignItems: "center", gap: 0.9, cursor: "pointer", px: 1.75, py: 0.8, borderRadius: "999px", border: "1.5px solid rgba(15,23,42,0.16)", bgcolor: "transparent", transition: "all 0.18s ease", "&:hover": { bgcolor: "rgba(15,23,42,0.04)", borderColor: "rgba(15,23,42,0.3)" } }}
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={INK} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 6 9 12 15 18" /></svg>
          <Typography sx={{ fontSize: "11px", fontWeight: 700, color: INK, fontFamily: F, lineHeight: 1 }}>Back</Typography>
        </Box>
      )}
      {hasNext && (
        <Box
          onClick={onScrollNext}
          sx={{ ml: hasPrev ? 0 : "auto", display: "inline-flex", alignItems: "center", gap: 0.9, cursor: "pointer", px: 1.75, py: 0.8, borderRadius: "999px", border: `1.5px solid ${TEAL}`, bgcolor: PAPER, transition: "all 0.18s ease", "&:hover": { bgcolor: `${TEAL}0d`, borderColor: TEAL_DARK } }}
        >
          <Typography sx={{ fontSize: "11px", fontWeight: 700, color: INK, fontFamily: F, lineHeight: 1 }}>Next</Typography>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={INK} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 6 15 12 9 18" /></svg>
        </Box>
      )}
    </Box>
  );
}

const CHAPTER_NAMES = {
  "intro": "The Research",
  "comfort-map": "The Comfort Map",
  "demographics": "Who's Comfortable",
  "momentum": "The Two-Year Shift",
  "summary": "The Road Ahead",
};

// Bottom-center map overlay: question + legend.
function MapQuestionLegend({ config }) {
  if (!config) return null;
  const { question, legend } = config;
  return (
    <Box sx={{
      position: "absolute", bottom: 14, left: "50%", transform: "translateX(-50%)", zIndex: 11,
      display: { xs: "none", sm: "flex" }, flexDirection: "column", alignItems: "center", gap: 0.7,
      maxWidth: "min(600px, calc(100% - 170px))", px: 1.75, py: 1.1, borderRadius: "14px",
      bgcolor: "rgba(255,255,255,0.9)", backdropFilter: "blur(12px)",
      border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 10px 32px rgba(15,23,42,0.12)",
      textAlign: "center", animation: "fadeUpIn 0.4s ease both", ...FADE_UP_KEYFRAMES,
    }}>
      <Typography sx={{ fontSize: "11px", fontWeight: 600, fontStyle: "italic", color: INK, fontFamily: F, lineHeight: 1.5 }}>
        {question}
      </Typography>
      {legend?.type === "ramp" && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", justifyContent: "center" }}>
          {legend.label && (
            <Typography sx={{ fontSize: "10px", fontWeight: 600, color: MUTED, fontFamily: F }}>{legend.label}</Typography>
          )}
          <Typography sx={{ fontSize: "9.5px", fontWeight: 700, color: MUTED_LIGHT, fontFamily: F }}>{legend.from || "Lower"}</Typography>
          <Box sx={{ width: 110, height: 8, borderRadius: "999px", background: `linear-gradient(90deg, ${legend.color}1c, ${legend.color})`, border: "1px solid rgba(15,23,42,0.06)" }} />
          <Typography sx={{ fontSize: "9.5px", fontWeight: 700, color: MUTED_LIGHT, fontFamily: F }}>{legend.to || "Higher"}</Typography>
        </Box>
      )}
      {legend?.type === "net" && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Typography sx={{ fontSize: "9.5px", fontWeight: 700, color: MUTED_LIGHT, fontFamily: F }}>Less comfortable</Typography>
          <Box sx={{ width: 110, height: 8, borderRadius: "999px", background: `linear-gradient(90deg, ${UNCOMF}, #f1f5f9, #2E7D32)`, border: "1px solid rgba(15,23,42,0.06)" }} />
          <Typography sx={{ fontSize: "9.5px", fontWeight: 700, color: MUTED_LIGHT, fontFamily: F }}>More comfortable</Typography>
        </Box>
      )}
    </Box>
  );
}

// ─── Card components ──────────────────────────────────────────────────────────
function IntroCard({ step, onScrollNext }) {
  const methodCards = [
    { title: "The sample", desc: "More than 8,000 American adults surveyed online in July 2026, balanced to be nationally representative by age, gender, ethnicity, education, party, and region." },
    { title: "The approach", desc: "MRP modeling links demographics to responses, then maps them onto every geography." },
    { title: "The output", desc: "Local comfort estimates for every state, county, and congressional district, tracked wave over wave." },
  ];

  return (
    <>
      <Box sx={fadeUp(0.05)}><StackLogo /></Box>
      <Box sx={{ ...fadeUp(0.1), maxWidth: 900, mx: "auto" }}>
        <Typography sx={{ fontSize: "10px", fontWeight: 700, letterSpacing: "0.22em", textTransform: "uppercase", color: TEAL, fontFamily: F, mb: 1.3 }}>
          The Research
        </Typography>
        <Typography sx={{ fontSize: { xs: "29px", sm: "37px", md: "44px" }, fontWeight: 500, lineHeight: { xs: 1.12, md: 1.08 }, letterSpacing: "-0.035em", color: INK, fontFamily: F, mb: 1.5 }}>
          {step.headline}
        </Typography>
        <Typography sx={{ fontSize: { xs: "15px", md: "19px" }, fontWeight: 400, color: "rgba(15,23,42,0.66)", fontFamily: F, lineHeight: 1.4, maxWidth: 820, mx: "auto" }}>
          {step.copy}
        </Typography>
      </Box>

      <Box sx={{ ...fadeUp(0.3), mt: 2.3 }}>
        <Typography sx={{ fontSize: "10px", fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase", color: "rgba(15,23,42,0.38)", fontFamily: F, mb: 1.15 }}>Methodology</Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(3, 1fr)" }, gap: 1.1 }}>
          {methodCards.map((c, i) => (
            <Box key={c.title} sx={{ position: "relative", overflow: "hidden", textAlign: "left", px: 1.6, pt: 1.5, pb: 1.4, borderRadius: "16px", border: `1px solid ${TEAL}`, bgcolor: "rgba(255,255,255,0.82)", boxShadow: "0 8px 24px rgba(15,23,42,0.04)" }}>
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.7, mb: 0.55 }}>
                <Typography sx={{ fontSize: "10px", fontWeight: 800, color: TEAL, fontFamily: F, letterSpacing: "0.08em" }}>{`0${i + 1}`}</Typography>
                <Typography sx={{ fontSize: "11.5px", fontWeight: 700, color: INK, fontFamily: F, letterSpacing: "0.01em" }}>{c.title}</Typography>
              </Box>
              <Typography sx={{ fontSize: "12px", color: "rgba(15,23,42,0.6)", fontFamily: F, lineHeight: 1.4 }}>{c.desc}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box
        onClick={onScrollNext}
        sx={{ ...fadeUp(0.38), mt: 2.8, display: { xs: "none", sm: "flex" }, alignItems: "center", justifyContent: "center", gap: 1, cursor: "pointer", userSelect: "none", "&:hover .scroll-label": { color: "rgba(15,23,42,0.68)" }, "&:hover .scroll-arrow": { borderColor: `${TEAL}88`, bgcolor: "rgba(255,255,255,0.9)" } }}
      >
        <Box className="scroll-arrow" sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: "50%", border: `1px solid ${TEAL}44`, bgcolor: "rgba(255,255,255,0.7)", transition: "border-color 0.2s ease, background-color 0.2s ease", animation: "scrollBounce 1.6s ease-in-out 1.5s infinite", "@keyframes scrollBounce": { "0%,100%": { transform: "translateY(0)" }, "50%": { transform: "translateY(5px)" } } }}>
          <svg width="9" height="9" viewBox="0 0 10 10" fill="none"><path d="M2 3.5L5 6.5L8 3.5" stroke={TEAL} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </Box>
        <Typography className="scroll-label" sx={{ fontSize: "11px", fontWeight: 600, color: "rgba(15,23,42,0.38)", fontFamily: F, letterSpacing: "0.1em", textTransform: "uppercase", transition: "color 0.2s ease" }}>Scroll for the first finding</Typography>
      </Box>
    </>
  );
}

function ComfortMapCard({ step, isActive, mainRef }) {
  const [wave, setWave] = useState(4);

  // Entering the slide: full reset to this slide's view. Same split as the
  // demographics card, and for the same reason — re-sending the reset block on
  // every wave flip made the map flash through a rescaled palette first, because
  // setHighlightsState writes heatDomain and heatColor as separate setState calls
  // ahead of the main one.
  useEffect(() => {
    if (!isActive) { setWave(4); return; }
    const inst = mainRef?.current;
    if (!inst) return;
    armCrossfade(getGlMap(inst));
    inst.setHighlightsState({ ...RESET_HIGHLIGHTS_STATE, ...step.mapView });
  }, [isActive, mainRef, setWave, step.mapView]);

  // Wave flips send only the wave, which falls through to the branch that just
  // writes waveSelect. One state write, so the crossfade tweens cleanly.
  const appliedWave = useRef(null);
  useEffect(() => {
    if (!isActive) { appliedWave.current = null; return; }
    if (appliedWave.current === wave) return;
    appliedWave.current = wave;
    const inst = mainRef?.current;
    if (!inst) return;
    armCrossfade(getGlMap(inst));
    inst.setHighlightsState({ wave });
  }, [isActive, mainRef, wave]);

  return (
    <>
      <StackLogo />
      <Eyebrow>The Comfort Map</Eyebrow>
      <Headline>{step.headline}</Headline>
      <BodyCopy>
        Nationally, <Em>36% are comfortable</Em> using a self-driving vehicle day to day, against <Em>49% uncomfortable</Em>. Only <Em>35 of 436 districts</Em> come out net-comfortable, and they are the youngest and most educated seats in the country. In April there were <Em>none at all</Em>: national net comfort has moved from -25 to -13 in a single quarter.
      </BodyCopy>

      <ClickHint
        mobileChildren={<><HintStrong>Switch the wave</HintStrong> below to watch comfort climb between April and July.</>}
      >
        <HintStrong>Hover any district</HintStrong> for its local numbers, and <HintStrong>switch the wave below</HintStrong> to watch comfort climb between April and July.
      </ClickHint>

      <Box sx={{ mt: 1.4, width: "100%", borderRadius: "16px", border: `1px solid ${BORDER}`, overflow: "hidden", bgcolor: PAPER, boxShadow: "0 8px 28px rgba(13,148,136,0.06)" }}>
        <Box sx={{ px: 2, py: 1.4, borderBottom: `1px solid ${BORDER}`, display: "flex", alignItems: "flex-start", gap: 1.25, flexWrap: "wrap" }}>
          <Box sx={{ flex: "1 1 240px", minWidth: 0 }}>
            <Typography sx={{ fontSize: { xs: "11.5px", sm: "9px" }, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: MUTED_LIGHT, fontFamily: F, mb: 0.6 }}>
              Survey question
            </Typography>
            <Typography sx={{ fontSize: { xs: "14px", sm: "12.5px" }, fontWeight: 600, color: INK, fontFamily: F, lineHeight: 1.4, fontStyle: "italic" }}>
              {QUESTION_TEXT.comfort}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 0.4, flexShrink: 0 }}>
            {[{ v: 3, l: "April (w3)" }, { v: 4, l: "July (w4)" }].map((o) => {
              const on = wave === o.v;
              return (
                <Box key={o.v} onClick={() => setWave(o.v)} sx={{ px: { xs: 1.4, sm: 1.15 }, py: { xs: 0.9, sm: 0.5 }, borderRadius: "999px", cursor: "pointer", userSelect: "none", fontSize: "11px", fontWeight: 700, fontFamily: F, lineHeight: 1, bgcolor: on ? TEAL : "transparent", color: on ? "#fff" : MUTED, border: `1px solid ${on ? TEAL : "rgba(15,23,42,0.12)"}`, transition: "all 0.18s ease", "&:hover": { borderColor: TEAL, color: on ? "#fff" : TEAL } }}>
                  {o.l}
                </Box>
              );
            })}
          </Box>
        </Box>

        <Box sx={{ px: 2, pt: 1.25 }}>
          <Typography sx={{ fontSize: { xs: "11.5px", sm: "9px" }, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: TEAL, fontFamily: F }}>
            National response ({wave === 3 ? "April" : "July"})
          </Typography>
        </Box>
        <Box sx={{ px: 2, pt: 1, pb: 1.6, display: "grid", gap: 0.85 }}>
          {COMFORT_ROWS_BY_WAVE[wave].map((r) => (
            <ResponseBarRow key={r.key} label={r.label} value={r.pct} color={r.color} />
          ))}
        </Box>
      </Box>

      <MethodNote />
    </>
  );
}

function DemographicsCard({ step, isActive, mainRef, age, setAge }) {
  // Entering the slide: full reset to this slide's map view. Deliberately does
  // NOT depend on `age`, so picking an age never re-runs it.
  useEffect(() => {
    if (!isActive) { setAge(null); return; }
    const inst = mainRef?.current;
    if (!inst) return;
    armCrossfade(getGlMap(inst));
    inst.setHighlightsState({ ...RESET_HIGHLIGHTS_STATE, ...step.mapView });
  }, [isActive, mainRef, setAge, step.mapView]);

  // Age changes send ONLY the demographic slice. Re-sending the reset block on
  // every click was what made the map flash through a wrong palette first:
  // setHighlightsState writes heatDomain, heatColor and topStrengthShading as
  // separate setState calls before the main one, so clearing them re-scaled the
  // ramp for a frame or two while the old age filter was still applied. Passing
  // `age` alone falls through to the branch that just writes the demo slice, so
  // it is one state write and the crossfade tweens straight to the new colours.
  // The ref keeps the very first run from re-applying an age the reset above
  // has already cleared.
  const appliedAge = useRef(null);
  useEffect(() => {
    if (!isActive) { appliedAge.current = null; return; }
    if (appliedAge.current === age) return;
    appliedAge.current = age;
    const inst = mainRef?.current;
    if (!inst) return;
    armCrossfade(getGlMap(inst));
    inst.setHighlightsState({ age });
  }, [isActive, mainRef, age]);

  return (
    <>
      <StackLogo />
      <Eyebrow>Who's Comfortable</Eyebrow>
      <Headline>{step.headline}</Headline>
      <BodyCopy>
        On the two-year question, 18–34s are net <Em>+43 "more comfortable"</Em> while 65+ are net <Em>-33</Em>. Today's comfort levels show the same shape even more starkly: +32 versus -58, a <Em>90-point generation gap</Em>. Education (+23 vs -9) and gender (+13 vs -4) matter, but age dwarfs them.
      </BodyCopy>

      <ClickHint
        mobileChildren={<><HintStrong>Tap an age</HintStrong> to re-map the two-year shift for that group.</>}
      >
        <HintStrong>Click an age below</HintStrong> to re-map the country's two-year shift for that group, and <HintStrong>hover any district</HintStrong> for its local numbers.
      </ClickHint>

      <Box sx={{ mt: 1.4, width: "100%", borderRadius: "16px", border: `1px solid ${BORDER}`, overflow: "hidden", bgcolor: PAPER, boxShadow: "0 8px 28px rgba(13,148,136,0.06)" }}>
        <Box sx={{ px: 2, py: 1.4, borderBottom: `1px solid ${BORDER}`, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography sx={{ flex: 1, fontSize: { xs: "11.5px", sm: "9px" }, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: TEAL, fontFamily: F }}>
            Map: two-year shift, by age
          </Typography>
          <Box sx={{ display: "flex", gap: 0.35, flexWrap: "wrap" }}>
            {[{ v: null, l: "Everyone" }, { v: 1, l: "18–34" }, { v: 2, l: "35–64" }, { v: 3, l: "65+" }].map((o) => {
              const on = age === o.v;
              return (
                <Box key={String(o.v)} onClick={() => setAge(o.v)} sx={{ px: { xs: 1.4, sm: 1.15 }, py: { xs: 0.9, sm: 0.5 }, borderRadius: "999px", cursor: "pointer", userSelect: "none", fontSize: "11px", fontWeight: 700, fontFamily: F, lineHeight: 1, bgcolor: on ? TEAL : "transparent", color: on ? "#fff" : MUTED, border: `1px solid ${on ? TEAL : "rgba(15,23,42,0.12)"}`, transition: "all 0.18s ease", "&:hover": { borderColor: TEAL, color: on ? "#fff" : TEAL } }}>
                  {o.l}
                </Box>
              );
            })}
          </Box>
        </Box>

        <Box sx={{ px: 2, pt: 1.4, pb: 1.6, display: "grid", gap: 1.4 }}>
          <Box sx={{ display: "grid", gap: 0.6 }}>
            <Typography sx={{ fontSize: { xs: "11.5px", sm: "9px" }, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: MUTED_LIGHT, fontFamily: F }}>
              Grown more comfortable (last two years)
            </Typography>
            {CHANGE_DEMO_ROWS.age.map((r) => <NetRow key={r.label} {...r} />)}
          </Box>
          <Box sx={{ display: "grid", gap: 0.6 }}>
            <Typography sx={{ fontSize: { xs: "11.5px", sm: "9px" }, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: MUTED_LIGHT, fontFamily: F }}>
              Comfortable today
            </Typography>
            {DEMO_NET_ROWS.age.map((r) => <NetRow key={r.label} {...r} />)}
          </Box>
          <Box sx={{ display: "grid", gap: 0.6 }}>
            <Typography sx={{ fontSize: { xs: "11.5px", sm: "9px" }, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: MUTED_LIGHT, fontFamily: F }}>
              The other gaps (two-year shift)
            </Typography>
            {[...CHANGE_DEMO_ROWS.education, ...CHANGE_DEMO_ROWS.gender, ...CHANGE_DEMO_ROWS.vote].map((r) => <NetRow key={r.label} {...r} />)}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.4, mt: 0.2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "2px", bgcolor: COMF }} />
              <Typography sx={{ fontSize: { xs: "11.5px", sm: "9.5px" }, color: MUTED, fontFamily: F }}>More / comfortable</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "2px", bgcolor: UNCOMF, opacity: 0.85 }} />
              <Typography sx={{ fontSize: { xs: "11.5px", sm: "9.5px" }, color: MUTED, fontFamily: F }}>Less / uncomfortable</Typography>
            </Box>
            <Typography sx={{ ml: "auto", fontSize: { xs: "11.5px", sm: "9.5px" }, color: MUTED_LIGHT, fontFamily: F }}>Net = green − red</Typography>
          </Box>
        </Box>
      </Box>

      <MethodNote />
    </>
  );
}

function MomentumCard({ step, isActive, mainRef, pick, setPick }) {
  // No setHighlightsState here — navigateToStep already owns this slide's map
  // view, so the boxes only add and remove the outline.
  useEffect(() => {
    if (!isActive) { setPick(null); return; }
    const inst = mainRef?.current;
    if (!inst) return;
    inst.setState({ chatHighlightAreas: pick ? MOMENTUM_CDS[pick] : [] });
  }, [isActive, mainRef, pick, setPick]);

  const selectPick = (key) => {
    const next = pick === key ? null : key;
    setPick(next);
    // Back to the country when nothing is outlined — MapPage's fit leaves the
    // camera framed on whichever set was last picked.
    if (!next && step.flyTo) mainRef?.current?.flyTo?.(flyForViewport(step.flyTo));
  };

  return (
    <>
      <StackLogo />
      <Eyebrow>The Two-Year Shift</Eyebrow>
      <Headline>{step.headline}</Headline>
      <BodyCopy>
        <Em>35% say they have grown more comfortable</Em> with self-driving cars over the last two years, against 31% less comfortable, and <Em>302 of 436 districts</Em> lean toward "more". The map below shades every district by its net two-year shift.
      </BodyCopy>

      <ClickHint
        mobileChildren={<><HintStrong>Tap either box below</HintStrong> to outline the districts it names, or <HintStrong>tap the expand button</HintStrong> to explore where comfort is growing fastest.</>}
      >
        <HintStrong>Hover any district</HintStrong> to see its two-year shift, and <HintStrong>click either box below</HintStrong> to outline the districts it names. The deepest greens are the fastest movers.
      </ClickHint>

      <Box sx={{ mt: 1.4, width: "100%", borderRadius: "16px", border: `1px solid ${BORDER}`, overflow: "hidden", bgcolor: PAPER, boxShadow: "0 8px 28px rgba(13,148,136,0.06)" }}>
        <Box sx={{ px: 2, py: 1.4, borderBottom: `1px solid ${BORDER}` }}>
          <Typography sx={{ fontSize: { xs: "11.5px", sm: "9px" }, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: MUTED_LIGHT, fontFamily: F, mb: 0.6 }}>
            Survey question
          </Typography>
          <Typography sx={{ fontSize: { xs: "14px", sm: "12.5px" }, fontWeight: 600, color: INK, fontFamily: F, lineHeight: 1.4, fontStyle: "italic" }}>
            {QUESTION_TEXT.change}
          </Typography>
        </Box>

        <Box sx={{ px: 2, pt: 1.25 }}>
          <Typography sx={{ fontSize: { xs: "11.5px", sm: "9px" }, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: TEAL, fontFamily: F }}>
            National response
          </Typography>
        </Box>
        <Box sx={{ px: 2, pt: 1, pb: 1.6, display: "grid", gap: 0.85 }}>
          {CHANGE_ROWS.map((r) => (
            <ResponseBarRow key={r.key} label={r.label} value={r.pct} color={r.color} />
          ))}
        </Box>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1, mt: 1.6 }}>
        <InsightBox
          color={TEAL}
          title="Fastest movers"
          desc="Manhattan's east side (NY-12) leads at net +29, with San Francisco (CA-11), Lower Manhattan (NY-10), Austin (TX-37), Denver (CO-01), and Seattle (WA-07) all above +25."
          onClick={() => selectPick("movers")}
          active={pick === "movers"}
          actionLabel="Click to outline these districts"
          activeLabel="Outlined — click to clear"
        />
        <InsightBox
          color={UNCOMF}
          title="Moving away"
          desc="A rural minority is cooling: eastern North Carolina (NC-01, -14), eastern Oklahoma (OK-02), and the Mississippi Delta (MS-02) have net-negative two-year shifts."
          onClick={() => selectPick("coolers")}
          active={pick === "coolers"}
          actionLabel="Click to outline these districts"
          activeLabel="Outlined — click to clear"
        />
      </Box>

      <MethodNote />
    </>
  );
}

function SummaryCard({ onExit }) {
  const takeaways = [
    { title: "The direction of travel is favourable, and it is moving fast", desc: "35% report growing more comfortable over two years against 31% less. In April not one district in the country had comfort outweighing discomfort. By July, 35 did." },
    { title: "The divide is generational, not political", desc: "Under-35s are net +43 more comfortable, over-65s net -33, a 90-point age gap on today's levels. Trump and Harris voters are 0.6 points apart." },
    { title: "35 of 436 districts are net-comfortable", desc: "Up from none at all in April. Nationally it is still 36% comfortable against 49% uncomfortable, and those 35 average 19% under-35 graduates against 8% across the other 401." },
  ];
  const nextSteps = [
    { title: "How do we message to them?", desc: "Creative and messenger testing with the movers: which voices and proof points hold the 18-34 urban cohort and unlock the 35-64 middle?" },
    { title: "Where is the ceiling?", desc: "Comfort riding is not the same as consent to remove the driver. Re-field the wheel and safety-bar questions to size the gap between who will ride and who will permit, district by district." },
    { title: "Does the momentum hold?", desc: "Track the same questions into the fall wave to see whether July's shift is a trend or a blip, district by district." },
  ];
  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", width: "100%" }}>
        <StackLogo />
        <Eyebrow>The Road Ahead</Eyebrow>
        <Typography sx={{ fontSize: { xs: "28px", sm: "34px" }, fontWeight: 500, color: INK, fontFamily: F, lineHeight: 1.1, letterSpacing: "-0.03em", mb: 2 }}>
          Three things to take with you.
        </Typography>
      </Box>
      <Box sx={{ display: "grid", gap: 1.1, width: "100%" }}>
        {takeaways.map((t, i) => (
          <Box key={t.title} sx={{ px: 1.7, py: 1.4, borderRadius: "16px", border: `1px solid ${BORDER}`, bgcolor: "rgba(255,255,255,0.9)", textAlign: "left" }}>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.8, mb: 0.4 }}>
              <Typography sx={{ fontSize: "11px", fontWeight: 800, color: TEAL, fontFamily: F }}>{`0${i + 1}`}</Typography>
              <Typography sx={{ fontSize: { xs: "14.5px", sm: "13px" }, fontWeight: 700, color: INK, fontFamily: F }}>{t.title}</Typography>
            </Box>
            <Typography sx={{ fontSize: { xs: "13.5px", sm: "12px" }, color: MUTED, fontFamily: F, lineHeight: 1.5 }}>{t.desc}</Typography>
          </Box>
        ))}
      </Box>

      <Typography sx={{ mt: 2.2, mb: 1, alignSelf: "flex-start", fontSize: { xs: "11.5px", sm: "10px" }, fontWeight: 800, letterSpacing: "0.16em", textTransform: "uppercase", color: TEAL, fontFamily: F }}>
        Where this goes next
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, gap: 1.1, width: "100%" }}>
        {nextSteps.map((t) => (
          <Box key={t.title} sx={{ px: 1.6, py: 1.3, borderRadius: "16px", border: `1px solid ${TEAL}30`, bgcolor: `${TEAL}06`, textAlign: "left" }}>
            <Typography sx={{ fontSize: { xs: "14px", sm: "12.5px" }, fontWeight: 700, color: INK, fontFamily: F, mb: 0.4, lineHeight: 1.3 }}>{t.title}</Typography>
            <Typography sx={{ fontSize: { xs: "13px", sm: "11.5px" }, color: MUTED, fontFamily: F, lineHeight: 1.5 }}>{t.desc}</Typography>
          </Box>
        ))}
      </Box>
      <Box
        onClick={onExit}
        sx={{ mt: 2.2, alignSelf: "center", display: "inline-flex", alignItems: "center", gap: 0.9, cursor: "pointer", px: 2.2, py: 1, borderRadius: "999px", border: `1.5px solid ${TEAL}`, bgcolor: PAPER, transition: "all 0.18s ease", "&:hover": { bgcolor: `${TEAL}0d` } }}
      >
        <Typography sx={{ fontSize: "13px", fontWeight: 700, color: INK, fontFamily: F, lineHeight: 1 }}>
          Explore the data in District Profiles
        </Typography>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={INK} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 6 15 12 9 18" /></svg>
      </Box>
      <MethodNote />
    </>
  );
}

// ─── NarrativeCard ────────────────────────────────────────────────────────────
function NarrativeCard({ step, index, total, isActive, cardRef, mainRef, onScrollNext, onScrollPrev, onPanelWheel, demoAge, setDemoAge, momentumPick, setMomentumPick, onExit }) {
  const isIntroHero = step.name === "intro";
  const isSummaryHero = step.name === "summary";
  const isOverlayHero = isIntroHero || isSummaryHero;
  // The summary carries three takeaways plus a three-across "where this goes next"
  // row, which is cramped at the 700px this used to be. Wider than the reading
  // column, still narrower than the intro hero.
  const heroMaxWidth = isIntroHero ? "min(1080px, 96vw)" : "min(980px, 94vw)";
  const nextStep = STEPS[index + 1];

  const heroOnXs = isIntroHero;
  const cardSx = {
    height: { xs: "auto", sm: "100vh" },
    minHeight: { xs: heroOnXs ? "100dvh" : "80vh", sm: isOverlayHero ? "100vh" : "auto" },
    flexShrink: 0,
    scrollSnapAlign: "start",
    scrollSnapStop: "always",
    scrollMarginTop: { xs: ["intro", "summary"].includes(step.name) ? 0 : MOBILE_STRIP_H, sm: 0 },
    mt: { xs: isIntroHero ? `-${MOBILE_STRIP_H}` : 0, sm: 0 },
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    pt: { xs: heroOnXs ? 0 : 3.5, sm: isOverlayHero ? 0 : 6, md: isOverlayHero ? 0 : 7 },
    pb: { xs: 5, sm: 5, md: 7 },
    px: { xs: isIntroHero ? 0 : isSummaryHero ? 1.25 : 2.5, sm: 3, md: 4.5 },
    position: "relative",
    overflowY: { xs: "visible", sm: isOverlayHero ? "hidden" : "auto" },
    borderLeft: "2px solid",
    borderColor: isOverlayHero ? "transparent" : (isActive ? `${TEAL}28` : "transparent"),
    opacity: isOverlayHero ? 1 : (isActive ? 1 : 0.38),
    transition: "opacity 0.4s ease, border-color 0.4s ease",
    background: isOverlayHero ? "transparent" : (isActive ? `linear-gradient(90deg, ${TEAL}05 0%, transparent 20%)` : "transparent"),
  };

  const inner = (() => {
    switch (step.name) {
      case "intro":        return <IntroCard step={step} onScrollNext={onScrollNext} />;
      case "comfort-map":  return <ComfortMapCard step={step} isActive={isActive} mainRef={mainRef} />;
      case "demographics": return <DemographicsCard step={step} isActive={isActive} mainRef={mainRef} age={demoAge} setAge={setDemoAge} />;
      case "momentum":     return <MomentumCard step={step} isActive={isActive} mainRef={mainRef} pick={momentumPick} setPick={setMomentumPick} />;
      case "summary":      return <SummaryCard onExit={onExit} />;
      default: return <><Headline>{step.headline}</Headline>{step.copy && <BodyCopy>{step.copy}</BodyCopy>}</>;
    }
  })();

  return (
    <Box ref={cardRef} data-index={index} sx={cardSx}>
      {isOverlayHero && isActive ? (
        <>
          {isSummaryHero && (
            <Box sx={{ display: { xs: "flex", sm: "none" }, flexDirection: "column", flex: 1, width: "100%" }}>
              {inner}
            </Box>
          )}
          <Box sx={{ display: { xs: isSummaryHero ? "none" : "flex", sm: "flex" }, position: { xs: "relative", sm: "fixed" }, inset: { xs: "auto", sm: "0 0 0 0" }, zIndex: 20, height: { xs: "auto", sm: "100vh" }, alignItems: "center", justifyContent: "center", px: { xs: 0, sm: 3, md: 5 }, py: { xs: 0, sm: 3 }, pointerEvents: "none" }}>
            <Panel
              data-hero-scroll="true"
              sx={{ maxWidth: { xs: "100%", sm: heroMaxWidth }, mx: "auto", maxHeight: { xs: "none", sm: "calc(100vh - 40px)" }, overflowY: { xs: "visible", sm: "auto" }, borderRadius: { xs: isIntroHero ? 0 : "22px", sm: "22px" }, p: { xs: 2.5, sm: 3.5, md: isIntroHero ? 4 : 3.5 }, textAlign: isIntroHero ? "center" : "left", pointerEvents: "auto", bgcolor: "rgba(255,255,255,0.85)", backgroundImage: "none", backdropFilter: "blur(20px)", border: { xs: isIntroHero ? "none" : "1px solid rgba(15,23,42,0.06)", sm: "1px solid rgba(15,23,42,0.06)" }, boxShadow: "0 26px 64px rgba(15,23,42,0.08)" }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", width: "100%", animation: "cardEnter 0.6s cubic-bezier(0.22,1,0.36,1) both", "@keyframes cardEnter": { from: { opacity: 0.3, transform: "translateY(10px)" }, to: { opacity: 1, transform: "translateY(0)" } } }}>
                {inner}
                {!isSummaryHero && <StepHeader index={index} total={total} chapter={CHAPTER_NAMES[step.name]} isActive={isActive} hasNext={!!nextStep} onScrollNext={onScrollNext} onScrollPrev={onScrollPrev} />}
              </Box>
            </Panel>
          </Box>
        </>
      ) : (
        <Box key={isActive ? "active" : "idle"} sx={{ display: "flex", flexDirection: "column", flex: 1, width: "100%", animation: isActive ? "cardEnter 0.45s cubic-bezier(0.22,1,0.36,1) both" : "none", "@keyframes cardEnter": { from: { opacity: 0.3, transform: "translateY(10px)" }, to: { opacity: 1, transform: "translateY(0)" } } }}>
          <Box sx={{ width: "100%", flex: { xs: "0 0 auto", sm: 1 }, display: "flex", flexDirection: "column", justifyContent: { xs: "flex-start", sm: "center" } }}>
            {inner}
          </Box>
          <StepHeader index={index} total={total} chapter={CHAPTER_NAMES[step.name]} isActive={isActive} hasNext={!!nextStep} onScrollNext={onScrollNext} onScrollPrev={onScrollPrev} />
        </Box>
      )}
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function SelfDrivingPage() {
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(0);
  const [mapReady, setMapReady] = useState(false);
  const [demoAge, setDemoAge] = useState(null);
  const [momentumPick, setMomentumPick] = useState(null);
  const cardRefs = useRef([]);
  const mainRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const navTimerRef = useRef(null);
  const targetIndexRef = useRef(null);

  // The comfort slide's wave toggle crosses the tracking CD-vintage cutoff
  // (CD119 for waves 1-3, CD120 from wave 4 — see utils/cdVersion), so every
  // 3↔4 flip makes MapPage reload a different ~15MB FlatGeobuf. MapPage's own
  // geoPreloadOrder only ever warms the vintage for the CURRENT wave, which is
  // wave 4, so the first flip back to wave 3 pays a full cold download and the
  // map visibly flashes through it.
  //
  // Warm the browser cache for the OTHER vintage so that first flip is a parse
  // rather than a download. Fire-and-forget on purpose: it must never block, and
  // a failure here just restores the old behaviour. The delay keeps it from
  // competing with the initial map load, which starts its own preload at 1.5s.
  useEffect(() => {
    const t = setTimeout(() => {
      const path = cdFgbPathFor("tracking", 3, { mobile: window.innerWidth < 600 });
      fetch(process.env.PUBLIC_URL + path, { cache: "force-cache" }).catch(() => {});
    }, 4000);
    return () => clearTimeout(t);
  }, []);
  const unlockTimerRef = useRef(null);
  useEffect(() => () => { if (unlockTimerRef.current) clearTimeout(unlockTimerRef.current); }, []);
  const visibleSet = useRef(new Set());
  const activeIndexRef = useRef(0);
  const mapPanelRef = useRef(null);
  const [mapFullscreen, setMapFullscreen] = useState(false);
  const [mobileMode, setMobileMode] = useState(window.innerWidth < 600);
  useEffect(() => {
    const onResize = () => setMobileMode(window.innerWidth < 600);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const isIntroStep = activeIndex === 0;
  const activeStep = STEPS[activeIndex];
  const isMapBackdropStep = isIntroStep || activeStep?.name === "summary";
  const hideMapOnXs = isIntroStep || activeStep?.name === "summary";

  useEffect(() => {
    if (hideMapOnXs) setMapFullscreen(false);
  }, [hideMapOnXs]);

  // Closing the fullscreen map must also dismiss any open tooltip.
  useEffect(() => {
    if (mapFullscreen) return;
    const mapPage = mainRef.current?._mapPageInstance;
    if (mapPage?.state?.hoverInfo) mapPage.setState({ hoverInfo: null });
  }, [mapFullscreen]);

  const mapFooterConfig = (() => {
    switch (activeStep?.name) {
      case "comfort-map":
        return { question: QUESTION_TEXT.comfort, legend: { type: "net" } };
      case "momentum":
      case "demographics":
        return { question: QUESTION_TEXT.change, legend: { type: "net" } };
      default:
        return null;
    }
  })();

  const mapWidth = { xs: "100%", sm: isMapBackdropStep ? "100%" : "47%", md: isMapBackdropStep ? "100%" : "50%" };
  const cardMr = { xs: 0, sm: isMapBackdropStep ? 0 : "47%", md: isMapBackdropStep ? 0 : "50%" };

  useEffect(() => {
    const t1 = setTimeout(() => { getGlMap(mainRef.current)?.resize(); }, 100);
    const t2 = setTimeout(() => { getGlMap(mainRef.current)?.resize(); }, 300);
    const t3 = setTimeout(() => {
      const glMap = getGlMap(mainRef.current);
      if (glMap) {
        glMap.resize();
        const step = STEPS[activeIndexRef.current];
        if (step?.flyTo) mainRef.current?.flyTo?.(flyForViewport(step.flyTo));
      }
    }, 500);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [isMapBackdropStep]); // eslint-disable-line react-hooks/exhaustive-deps

  const scrollToIndex = useCallback((i) => {
    const card = cardRefs.current[i];
    if (!card) return;
    const container = scrollContainerRef.current;
    if (container) container._scrollLocked = true;
    targetIndexRef.current = i;
    activeIndexRef.current = i;
    visibleSet.current = new Set([i]);
    setActiveIndex(i);
    card.scrollIntoView({ behavior: "smooth", block: "start" });
    if (unlockTimerRef.current) clearTimeout(unlockTimerRef.current);
    unlockTimerRef.current = window.setTimeout(() => {
      unlockTimerRef.current = null;
      if (container) container._scrollLocked = false;
      targetIndexRef.current = null;
      const el = cardRefs.current[i];
      if (!el) return;
      if (window.innerWidth >= 600 && container) {
        const offset = el.getBoundingClientRect().top - container.getBoundingClientRect().top;
        if (Math.abs(offset) > 4) el.scrollIntoView({ behavior: "auto", block: "start" });
      } else if (window.innerWidth < 600) {
        const expectedTop = parseFloat(getComputedStyle(el).scrollMarginTop) || 0;
        if (Math.abs(el.getBoundingClientRect().top - expectedTop) > 4) {
          el.scrollIntoView({ behavior: "auto", block: "start" });
        }
      }
    }, 1200);
  }, []);

  const getNearestCardIndex = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || !cardRefs.current.length) return null;
    const containerTop = container.getBoundingClientRect().top;
    let nearestIdx = null;
    let nearestDistance = Infinity;
    cardRefs.current.forEach((card, idx) => {
      if (!card) return;
      const distance = Math.abs(card.getBoundingClientRect().top - containerTop);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIdx = idx;
      }
    });
    return nearestIdx;
  }, []);

  // Sliding cooldown: momentum events keep pushing the re-arm back so one
  // physical flick can never advance two slides.
  const wheelCooldownRef = useRef(false);
  const wheelDeltaRef = useRef(0);
  const lastWheelTsRef = useRef(0);
  const wheelNavAtRef = useRef(0);
  const handleRightPanelWheel = useCallback((e) => {
    if (window.innerWidth < 600) return;
    const container = scrollContainerRef.current;
    if (!container) return;

    const heroEl = e.target instanceof Element ? e.target.closest("[data-hero-scroll]") : null;
    if (heroEl instanceof HTMLElement) {
      const canScrollDown = heroEl.scrollTop + heroEl.clientHeight < heroEl.scrollHeight - 2;
      const canScrollUp = heroEl.scrollTop > 2;
      if ((e.deltaY > 0 && canScrollDown) || (e.deltaY < 0 && canScrollUp)) {
        wheelDeltaRef.current = 0;
        return;
      }
    }

    const cardEl = e.target instanceof Element ? e.target.closest("[data-index]") : null;
    if (cardEl instanceof HTMLElement) {
      const canScrollDown = cardEl.scrollTop + cardEl.clientHeight < cardEl.scrollHeight - 2;
      const canScrollUp = cardEl.scrollTop > 2;
      if ((e.deltaY > 0 && canScrollDown) || (e.deltaY < 0 && canScrollUp)) {
        wheelDeltaRef.current = 0;
        return;
      }
    }

    e.preventDefault();
    const now = Date.now();
    const gap = now - lastWheelTsRef.current;
    lastWheelTsRef.current = now;
    if (container._scrollLocked) return;
    if (wheelCooldownRef.current) {
      // Release on a 250ms pause in the wheel stream OR a 1.6s hard cap,
      // whichever comes first: the pause catches deliberate second flicks
      // instantly, the cap stops long free-spin inertia from locking the
      // reel up (native scroll is genuinely prevented now, so a rare
      // late re-nav is at worst intended input).
      const sinceNav = now - wheelNavAtRef.current;
      if (gap < 250 && sinceNav < 1600) return;
      wheelCooldownRef.current = false;
    }

    wheelDeltaRef.current += e.deltaY;
    if (Math.abs(wheelDeltaRef.current) < 70) return;

    const currentIdx = targetIndexRef.current ?? getNearestCardIndex();
    if (currentIdx == null) return;

    const dir = wheelDeltaRef.current > 0 ? 1 : -1;
    const nextIdx = Math.max(0, Math.min(STEPS.length - 1, currentIdx + dir));
    if (nextIdx === currentIdx) {
      wheelDeltaRef.current = 0;
      return;
    }

    wheelDeltaRef.current = 0;
    wheelCooldownRef.current = true;
    wheelNavAtRef.current = Date.now();
    scrollToIndex(nextIdx);
  }, [getNearestCardIndex, scrollToIndex]);

  // React's onWheel is attached PASSIVELY at the root, so preventDefault was
  // silently ignored: native inertia scroll ran alongside the programmatic
  // snap (double-advance on large screens, left side only — the map panel has
  // nothing to scroll natively). Attach non-passive listeners ourselves so
  // preventDefault actually blocks the native scroll. Column listener also
  // covers the hero panels via bubbling.
  useEffect(() => {
    const els = [scrollContainerRef.current, mapPanelRef.current].filter(Boolean);
    els.forEach((el) => el.addEventListener("wheel", handleRightPanelWheel, { passive: false }));
    return () => els.forEach((el) => el.removeEventListener("wheel", handleRightPanelWheel));
  }, [handleRightPanelWheel]);


  useEffect(() => {
    activeIndexRef.current = activeIndex;
    if (navTimerRef.current) clearTimeout(navTimerRef.current);
    navTimerRef.current = setTimeout(() => {
      navTimerRef.current = null;
      const stepName = STEPS[activeIndex]?.name;
      if (stepName) {
        navigateToStep(mainRef.current, stepName);
      }
    }, 160);
  }, [activeIndex]);

  // Readiness poll: reveal once metric data + cd geometry are loaded, then
  // re-fire the active step. loadGeo recovery matches keys by prefix (they
  // carry the CD vintage, e.g. "cd::CD120").
  useEffect(() => {
    let fired = false;
    const poll = setInterval(() => {
      const inst = mainRef.current;
      if (!inst) return;
      const loading = inst.state?.mapLoading;
      const mapPage = inst._mapPageInstance;
      const cdLoaded = mapPage?.state?.geoPreloadStatus?.cd === "loaded";
      if (!fired && loading === false && cdLoaded) {
        const featureCount = mapPage?.state?.geoData?.features?.length ?? 0;
        if (featureCount < 200 && mapPage) {
          mapPage.loadGeo("cd", null, { doFit: false });
        }
        fired = true;
        clearInterval(poll);
        clearTimeout(fallback);
        setMapReady(true);
        const stepName = STEPS[activeIndexRef.current]?.name;
        if (stepName) {
          navigateToStep(inst, stepName);
        }
      }
    }, 150);
    const fallback = setTimeout(() => setMapReady(true), 30000);
    return () => { clearInterval(poll); clearTimeout(fallback); };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const container = scrollContainerRef.current;
        const locked = !!container?._scrollLocked;
        if (window.innerWidth < 600) {
          entries.forEach((entry) => {
            const idx = Number(entry.target.dataset.index);
            if (entry.isIntersecting) visibleSet.current.add(idx);
            else visibleSet.current.delete(idx);
          });
          if (!locked && visibleSet.current.size > 0) setActiveIndex(Math.max(...visibleSet.current));
        } else {
          if (locked) return;
          const nearestIdx = getNearestCardIndex();
          if (nearestIdx != null) setActiveIndex(nearestIdx);
        }
      },
      { root: mobileMode ? null : scrollContainerRef.current, rootMargin: "0px 0px -40% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    cardRefs.current.forEach((card) => { if (card) observer.observe(card); });
    return () => observer.disconnect();
  }, [getNearestCardIndex, mobileMode]);

  useEffect(() => { activeIndexRef.current = activeIndex; }, [activeIndex]);

  // Desktop resize re-anchor: cards are viewport-sized but scrollTop is pixels,
  // so growing the window parks the column mid-card (and the next wheel flick
  // then looks like it skips two screens). Re-align the active card whenever
  // the window settles at a new size; the map resizes with it.
  useEffect(() => {
    let t;
    const onResize = () => {
      clearTimeout(t);
      t = setTimeout(() => {
        if (window.innerWidth < 600) return; // the mobile strip has its own refit
        const container = scrollContainerRef.current;
        const el = cardRefs.current[activeIndexRef.current];
        if (container && el && !container._scrollLocked) {
          el.scrollIntoView({ behavior: "auto", block: "start" });
        }
        getGlMap(mainRef.current)?.resize();
      }, 180);
    };
    window.addEventListener("resize", onResize);
    return () => { window.removeEventListener("resize", onResize); clearTimeout(t); };
  }, []);


  const resizeAndRefit = useCallback(() => {
    let tries = 0;
    const attempt = () => {
      const map = getGlMap(mainRef.current);
      if (!map) {
        if (tries++ < 20) setTimeout(attempt, 100);
        return;
      }
      map.resize();
      const step = STEPS[activeIndexRef.current];
      if (step?.flyTo) mainRef.current?.flyTo?.(flyForViewport(step.flyTo));
    };
    attempt();
  }, []);

  useEffect(() => {
    if (!mobileMode) return;
    const raf = requestAnimationFrame(resizeAndRefit);
    const settle = setTimeout(resizeAndRefit, 350);
    let debounce;
    let ro;
    const el = mapPanelRef.current;
    if (el && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => {
        clearTimeout(debounce);
        debounce = setTimeout(resizeAndRefit, 150);
      });
      ro.observe(el);
    }
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(settle);
      clearTimeout(debounce);
      if (ro) ro.disconnect();
    };
  }, [mobileMode, resizeAndRefit]);

  useEffect(() => {
    if (window.innerWidth >= 600) return;
    resizeAndRefit();
    const t = setTimeout(resizeAndRefit, 350);
    return () => clearTimeout(t);
  }, [mapFullscreen, resizeAndRefit]);

  const isBackdropBlurStep = isMapBackdropStep;

  return (
    <Box sx={{ bgcolor: BG, height: { xs: "auto", sm: "100vh" }, overflow: { xs: "visible", sm: "hidden" } }}>
      {/* Branding / exit */}
      <Box sx={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 1100, display: "flex", alignItems: "center", px: 2, py: 1, pointerEvents: "none" }}>
        <Box sx={{ height: 3, position: "absolute", top: 0, left: 0, bgcolor: TEAL, width: `${(activeIndex / (STEPS.length - 1)) * 100}%`, transition: "width 0.4s ease" }} />
        <Box onClick={() => exitHighlights(navigate)} sx={{ ml: "auto", width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "8px", cursor: "pointer", pointerEvents: "auto", color: "rgba(15,23,42,0.45)", bgcolor: "rgba(255,255,255,0.8)", backdropFilter: "blur(8px)", border: "1px solid rgba(15,23,42,0.06)", "&:hover": { color: TEAL }, transition: "all 0.15s ease" }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
        </Box>
      </Box>

      {/* Map panel: right half on desktop, 30dvh strip on phones */}
      <Box ref={mapPanelRef} sx={{
        display: { xs: hideMapOnXs ? "none" : "block", sm: "block" },
        position: "fixed",
        top: 0,
        left: { xs: 0, sm: isMapBackdropStep ? 0 : "auto" },
        right: 0,
        width: mapWidth,
        height: { xs: mapFullscreen ? "100dvh" : MOBILE_STRIP_H, sm: "100vh" },
        zIndex: { xs: mapFullscreen ? 1300 : 5, sm: 5 },
        overflow: "hidden",
        borderBottom: { xs: "0.5px solid rgba(15,23,42,0.08)", sm: "none" },
        borderLeft: { sm: isMapBackdropStep ? "none" : "0.5px solid rgba(15,23,42,0.06)" },
        bgcolor: "background.paper",
        "@media (max-width: 599.95px)": {
          "& .mapboxgl-ctrl-attrib": { display: "none" },
        },
        transition: "width 0.75s cubic-bezier(0.22,1,0.36,1), height 0.55s cubic-bezier(0.22,1,0.36,1), left 0.75s cubic-bezier(0.22,1,0.36,1)",
      }}>
        <Box sx={{ height: "100%", filter: isBackdropBlurStep ? "blur(10px) saturate(0.8)" : "none", transform: isBackdropBlurStep ? "scale(1.04)" : "none", transition: "filter 0.75s cubic-bezier(0.22,1,0.36,1), transform 0.75s cubic-bezier(0.22,1,0.36,1)" }}>
          <AppPanelWrapper ref={mainRef} interactive={mapFullscreen} />
        </Box>

        {/* Hero blocker — no hover tooltips on blurred-backdrop steps */}
        <Box sx={{ position: "absolute", inset: 0, zIndex: 8, cursor: "default", pointerEvents: isBackdropBlurStep ? "auto" : "none" }} />

        {/* Loading overlay */}
        <Box sx={{ position: "absolute", inset: 0, zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#ffffff", opacity: mapReady ? 0 : 1, pointerEvents: mapReady ? "none" : "auto", transition: "opacity 0.6s ease" }}>
          <CircularProgress size={28} thickness={3.5} sx={{ color: TEAL }} />
        </Box>

        {/* The wave-over-wave stat is a statement about this map, so it rides on
            top of it rather than sitting in the scroll column. Comfort slide only. */}
        {activeStep?.name === "comfort-map" && <WaveMoveStat />}

        <MapQuestionLegend config={mapFooterConfig} />

        {/* Mobile fullscreen toggle */}
        <Box
          onClick={() => setMapFullscreen((f) => !f)}
          sx={{ display: { xs: "flex", sm: "none" }, position: "absolute", bottom: mapFullscreen ? "calc(12px + env(safe-area-inset-bottom))" : 12, right: 12, zIndex: 20, width: 36, height: 36, borderRadius: "10px", bgcolor: mapFullscreen ? TEAL : "rgba(255,255,255,0.92)", backdropFilter: "blur(8px)", border: mapFullscreen ? `1px solid ${TEAL}` : `1px solid ${TEAL}33`, boxShadow: mapFullscreen ? "0 2px 14px rgba(13,148,136,0.4)" : "0 2px 12px rgba(0,0,0,0.08)", alignItems: "center", justifyContent: "center", cursor: "pointer", userSelect: "none", WebkitTapHighlightColor: "transparent" }}
        >
          {mapFullscreen ? (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          ) : (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={INK} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" /><line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" /></svg>
          )}
        </Box>
      </Box>

      {/* Dot nav — desktop left rail */}
      <Box sx={{ display: { xs: "none", sm: "flex" }, flexDirection: "column", alignItems: "center", gap: 1, position: "fixed", left: 14, top: "50%", transform: "translateY(-50%)", zIndex: 1050 }}>
        {STEPS.map((s, i) => (
          <Box key={i} onClick={() => scrollToIndex(i)} title={s.headline?.slice(0, 48)} sx={{ width: activeIndex === i ? 8 : 5, height: activeIndex === i ? 8 : 5, borderRadius: "50%", bgcolor: activeIndex === i ? TEAL : `${TEAL}30`, transition: "all 0.25s ease", cursor: "pointer", "&:hover": { bgcolor: TEAL, transform: "scale(1.35)" } }} />
        ))}
      </Box>

      {/* Mobile bottom nav bar */}
      <Box sx={{ display: { xs: "flex", sm: "none" }, position: "fixed", bottom: 0, left: 0, right: 0, height: "calc(48px + env(safe-area-inset-bottom))", pb: "env(safe-area-inset-bottom)", bgcolor: "rgba(255,255,255,0.95)", backdropFilter: "blur(10px)", borderTop: "0.5px solid rgba(15,23,42,0.08)", zIndex: 1050, alignItems: "stretch", justifyContent: "space-between" }}>
        <Box onClick={() => { if (activeIndex > 0) scrollToIndex(activeIndex - 1); }} sx={{ display: "flex", alignItems: "center", gap: 0.75, px: 2.5, cursor: activeIndex === 0 ? "default" : "pointer", opacity: activeIndex === 0 ? 0.25 : 1, WebkitTapHighlightColor: "transparent" }}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 6 9 12 15 18" /></svg>
          <Typography sx={{ fontSize: "14px", fontWeight: 700, color: INK, fontFamily: F }}>Prev</Typography>
        </Box>
        <Typography sx={{ alignSelf: "center", fontSize: "11px", fontWeight: 600, color: MUTED, fontFamily: F, letterSpacing: "0.05em" }}>{activeIndex + 1} / {STEPS.length}</Typography>
        <Box onClick={() => { if (activeIndex < STEPS.length - 1) scrollToIndex(activeIndex + 1); }} sx={{ display: "flex", alignItems: "center", gap: 0.75, px: 2.5, cursor: activeIndex === STEPS.length - 1 ? "default" : "pointer", opacity: activeIndex === STEPS.length - 1 ? 0.25 : 1, WebkitTapHighlightColor: "transparent" }}>
          <Typography sx={{ fontSize: "14px", fontWeight: 700, color: INK, fontFamily: F }}>Next</Typography>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 6 15 12 9 18" /></svg>
        </Box>
      </Box>

      {/* Narrative scroll column */}
      <Box ref={scrollContainerRef} sx={{ mr: cardMr, pt: { xs: MOBILE_STRIP_H, sm: 0 }, pb: { xs: "calc(68px + env(safe-area-inset-bottom))", sm: 0 }, height: { xs: "auto", sm: "100vh" }, overflowY: { xs: "visible", sm: "scroll" }, scrollSnapType: { xs: "none", sm: "y mandatory" }, "&::-webkit-scrollbar": { display: "none" }, msOverflowStyle: "none", scrollbarWidth: "none", transition: "margin-right 0.75s cubic-bezier(0.22,1,0.36,1)" }}>
        {STEPS.map((step, i) => (
          <NarrativeCard
            key={step.name}
            step={step}
            index={i}
            total={STEPS.length}
            isActive={activeIndex === i}
            cardRef={(el) => { cardRefs.current[i] = el; }}
            mainRef={mainRef}
            onScrollNext={() => scrollToIndex(Math.min(STEPS.length - 1, i + 1))}
            onScrollPrev={() => scrollToIndex(Math.max(0, i - 1))}
            onPanelWheel={handleRightPanelWheel}
            demoAge={demoAge}
            setDemoAge={setDemoAge}
            momentumPick={momentumPick}
            setMomentumPick={setMomentumPick}
            onExit={() => exitHighlights(navigate)}
          />
        ))}
      </Box>
    </Box>
  );
}
